import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const VerifyEmailChange = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const verifyEmailChange = async () => {
      try {
        const response = await api.post(`/user/verify-email-change/${token}`);
        setSuccess(true);
        setNewEmail(response.data.new_email);
        
        setTimeout(() => {
          navigate('/connexion');
        }, 5000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Lien de vérification invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };

    verifyEmailChange();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Vérification en cours...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-[#16161A] rounded-xl shadow-xl border border-[#3A3A3D] p-8 text-center"
        >
          <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Erreur de vérification</h2>
          <p className="text-[#B8B8BA] mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all"
          >
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-[#16161A] rounded-xl shadow-xl border border-[#3A3A3D] p-8 text-center"
        >
          <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Email vérifié !</h2>
          <div className="mb-6">
            <p className="text-[#B8B8BA] mb-2">Votre nouvelle adresse email a été confirmée :</p>
            <p className="text-[#00FFFF] font-semibold text-lg">{newEmail}</p>
          </div>
          <p className="text-[#B8B8BA] text-sm mb-6">
            Vous allez être redirigé vers la page de connexion dans quelques secondes...
          </p>
          <button
            onClick={() => navigate('/connexion')}
            className="bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all"
          >
            Se connecter maintenant
          </button>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default VerifyEmailChange;
